import { FiZap } from 'react-icons/fi';

export const skimmingScanningData = {
  courseId: 'skimming-scanning',
  courseType: 'reading-course',
  title: 'Skimming & Scanning',
  icon: FiZap,
  overview: {
    impact: 'Essential for time management in Reading',
    focus: 'Speed reading techniques',
    uses: 'All reading question types',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Skimming vs Scanning',
      content: `<p>These are two different speed-reading techniques with different purposes:</p>`,
      table: {
        headers: ['Technique', 'Purpose', 'Speed', 'When to Use'],
        rows: [
          ['Skimming', 'Get main idea/overview', 'Very fast', 'First read of passage'],
          ['Scanning', 'Find specific information', 'Fast', 'Answering detail questions'],
        ],
      },
    },
    {
      title: 'How to Skim Effectively',
      content: `<p>Skimming helps you understand the passage structure in 2-3 minutes:</p>`,
      points: [
        'Read the title and any subheadings',
        'Read the first paragraph completely',
        'Read the first sentence of each following paragraph',
        'Read the last paragraph completely',
        'Note key topic words that repeat',
        'Identify the passage type: argument, explanation, comparison, etc.',
      ],
      warning: 'Don\'t read every word! You\'re looking for the big picture.',
    },
    {
      title: 'How to Scan Effectively',
      content: `<p>Scanning helps you find specific answers quickly:</p>`,
      points: [
        'Identify key words in the question first',
        'Look for those exact words OR synonyms in the text',
        'Move your eyes quickly down the page',
        'When you find the area, slow down and read carefully',
        'Use capital letters, numbers, and special formatting as anchors',
      ],
      examples: [
        { 
          label: 'Question', 
          text: 'In which year was the study conducted?' 
        },
        { 
          label: 'Scan for', 
          text: 'Numbers (dates), words like "study", "research", "conducted"' 
        },
      ],
    },
    {
      title: 'Combining Both Techniques',
      content: `<p>Use this approach for each IELTS Reading passage:</p>`,
      points: [
        '1. SKIM the passage first (2-3 minutes) — understand structure and main ideas',
        '2. Read the questions — note key words',
        '3. SCAN for answers — find the relevant paragraph',
        '4. Read carefully — understand the detail for the answer',
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify What to Scan For',
      type: 'select',
      instructions: 'What would you scan for to answer each question?',
      questions: [
        {
          text: 'Question: "When was the university founded?"',
          options: ['Numbers/Dates', 'Names', 'Descriptions'],
          answer: 'Numbers/Dates',
          explanation: '"When" indicates you need a date or year.',
        },
        {
          text: 'Question: "Who led the research team?"',
          options: ['Names (capital letters)', 'Numbers', 'Places'],
          answer: 'Names (capital letters)',
          explanation: '"Who" indicates a person\'s name (capital letters).',
        },
        {
          text: 'Question: "What percentage of participants agreed?"',
          options: ['Numbers with %', 'Names', 'Locations'],
          answer: 'Numbers with %',
          explanation: '"Percentage" indicates numbers with % symbol.',
        },
        {
          text: 'Question: "Where did the experiment take place?"',
          options: ['Places/Locations', 'Dates', 'Names'],
          answer: 'Places/Locations',
          explanation: '"Where" indicates a location (often capitalized).',
        },
        {
          text: 'Question: "How many species were identified?"',
          options: ['Numbers', 'Names', 'Descriptions'],
          answer: 'Numbers',
          explanation: '"How many" indicates a number or quantity.',
        },
      ],
    },
    {
      title: 'Scanning Strategy',
      type: 'select',
      instructions: 'What is the best scanning strategy for each question type?',
      questions: [
        {
          text: 'Finding names of researchers in a scientific passage',
          options: ['Look for capital letters', 'Read every paragraph', 'Focus on dates'],
          answer: 'Look for capital letters',
          explanation: 'Names are capitalized, making them easy to spot.',
        },
        {
          text: 'Finding specific statistics',
          options: ['Scan for numbers, %, figures', 'Read the conclusion', 'Look for opinions'],
          answer: 'Scan for numbers, %, figures',
          explanation: 'Statistics contain numbers and symbols like %.',
        },
        {
          text: 'Finding a definition of a technical term',
          options: ['Look for "is defined as," "refers to," italics', 'Read chronologically', 'Scan for dates'],
          answer: 'Look for "is defined as," "refers to," italics',
          explanation: 'Definitions often have signal phrases or formatting.',
        },
      ],
    },
    {
      title: 'Skimming Questions',
      type: 'select',
      instructions: 'What should you focus on when skimming a passage?',
      questions: [
        {
          text: 'When skimming a passage for the first time, you should:',
          options: ['Read first and last paragraphs, first sentences of others', 'Read every word carefully', 'Focus only on examples'],
          answer: 'Read first and last paragraphs, first sentences of others',
          explanation: 'This gives you the main idea and structure quickly.',
        },
        {
          text: 'Subheadings help you:',
          options: ['Understand the passage structure', 'Find specific answers', 'Memorize vocabulary'],
          answer: 'Understand the passage structure',
          explanation: 'Subheadings reveal how information is organized.',
        },
        {
          text: 'How long should skimming take for one passage?',
          options: ['2-3 minutes', '10 minutes', '30 seconds'],
          answer: '2-3 minutes',
          explanation: '2-3 minutes gives you enough time to understand structure.',
        },
      ],
    },
    {
      title: 'Identify Question Keywords',
      type: 'fill-in',
      instructions: 'What key words would you scan for in each question?',
      questions: [
        {
          text: '"In which year was the first vaccine developed?"',
          answer: 'year, vaccine, first',
          explanation: 'Scan for dates, "vaccine," and signals like "first" or "original."',
        },
        {
          text: '"What does the author suggest about climate change?"',
          answer: 'author, suggest, climate change',
          explanation: 'Look for opinion language and "climate change" mentions.',
        },
        {
          text: '"How many countries participated in the study?"',
          answer: 'countries, participated, number',
          explanation: 'Scan for numbers and words like "countries" or "nations."',
        },
      ],
    },
    {
      title: 'Skimming Practice',
      type: 'writing',
      instructions: 'What would you focus on when skimming these elements?',
      questions: [
        {
          text: 'A passage titled "The Rise and Fall of Ancient Rome"',
          example: 'Focus on: chronological structure, key events, time periods mentioned in first sentences',
          tip: '"Rise and Fall" suggests a historical narrative with stages.',
        },
        {
          text: 'A passage with subheadings: "Benefits", "Drawbacks", "Conclusion"',
          example: 'Focus on: argumentative structure, points for/against, final position',
          tip: 'Subheadings reveal this is a pros/cons comparison.',
        },
        {
          text: 'A scientific passage with graphs and data tables',
          example: 'Focus on: introduction for hypothesis, conclusion for findings, labels on graphs',
          tip: 'Visual data often contains key information for specific questions.',
        },
      ],
    },
  ],
  tips: [
    'Practice skimming articles in 2 minutes or less',
    'Build a mental map of where information is located',
    'Use paragraph first sentences as your guide',
    'Train your eyes to move quickly without reading every word',
  ],
  dailyPractice: [
    'Skim a news article and summarize the main idea',
    'Scan a text for all numbers, then check accuracy',
    'Time yourself: skim a passage in 2 minutes',
    'Practice finding specific facts in dense texts',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext: 'Skimming & Scanning: Speed reading techniques for IELTS Reading. Skimming for main ideas and structure; scanning for specific details.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...skimmingScanningData,
      title: skimmingScanningData.title + ' (Foundations)',
      overview: {
        ...skimmingScanningData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: skimmingScanningData.sections.slice(0, Math.max(2, Math.ceil(skimmingScanningData.sections.length / 2))),
      exercises: skimmingScanningData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...skimmingScanningData,
      title: skimmingScanningData.title + ' (Advanced)',
      overview: {
        ...skimmingScanningData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: skimmingScanningData.sections,
      exercises: skimmingScanningData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(skimmingScanningData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return skimmingScanningData;
}
